/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/**
 * Local lightweight JWT payload decoder to avoid dependency on jwt-decode v3/v4 differences
 */

function base64UrlDecodeToString(input: string): string {
  const base64 = input
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(input.length / 4) * 4, '=');

  if (typeof atob === 'function') {
    const binary = atob(base64);
    try {
      return decodeURIComponent(
        Array.prototype.map
          .call(
            binary,
            (c: string) =>
              '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
          )
          .join(''),
      );
    } catch {
      throw new Error('Invalid JWT payload: Malformed UTF-8 string');
    }
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('utf-8');
  }

  throw new Error('Base64 decode not supported in this environment');
}

/**
 * Decodes a JWT token and returns the payload as a typed object.
 * This is a lightweight alternative to jwt-decode that works across different environments.
 *
 * @param token - The JWT token string to decode
 * @returns The decoded payload as a typed object
 * @throws Error if the token is invalid or base64 decoding is not supported
 */
export function jwtDecode<T extends object = Record<string, unknown>>(
  token: string,
): T {
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) {
    throw new Error('Invalid JWT token');
  }

  const payload = parts[1];
  const json = base64UrlDecodeToString(payload);
  return JSON.parse(json) as T;
}
